export type ReservableSpot = {
  id: number | string;
  name: string;
  is_free?: boolean;
  is_able?: boolean;
  reason?: string;
  available_count?: number;
  [key: string]: unknown;
};

export type SelectedReservationSpot = ReservableSpot & {
  selectionKey: string;
  selectionIndex: number;
};

type ReservationRequestParams = {
  phoneNum: string;
  reservDate: string;
  reservProduct: string;
  spots: SelectedReservationSpot[];
  paidAmount: number | string;
  notifyUser: boolean;
  customerDisplayName?: string;
};

export function getSpotIdentity(spot: ReservableSpot) {
  return String(spot.id ?? spot.name);
}

export function getSelectedSpotCount(spots: SelectedReservationSpot[], targetSpot: ReservableSpot) {
  const targetIdentity = getSpotIdentity(targetSpot);
  return spots.filter((spot) => getSpotIdentity(spot) === targetIdentity).length;
}

export function createOpenSeatSelections(spot: ReservableSpot, quantity: number): SelectedReservationSpot[] {
  return Array.from({ length: Math.max(0, quantity) }, (_, index) => ({
    ...spot,
    selectionIndex: index + 1,
    selectionKey: `${getSpotIdentity(spot)}:open:${index + 1}`,
  }));
}

export function addOpenSeatSelection(
  spots: SelectedReservationSpot[],
  targetSpot: ReservableSpot,
): SelectedReservationSpot[] {
  const nextIndex = getSelectedSpotCount(spots, targetSpot) + 1;
  return [
    ...spots,
    {
      ...targetSpot,
      selectionIndex: nextIndex,
      selectionKey: `${getSpotIdentity(targetSpot)}:open:${nextIndex}`,
    },
  ];
}

export function removeOpenSeatSelection(
  spots: SelectedReservationSpot[],
  targetSpot: ReservableSpot,
): SelectedReservationSpot[] {
  const targetIdentity = getSpotIdentity(targetSpot);
  const reversedIndex = [...spots]
    .reverse()
    .findIndex((spot) => getSpotIdentity(spot) === targetIdentity);
  if (reversedIndex === -1) return spots;

  const removeIndex = spots.length - 1 - reversedIndex;
  return spots.filter((_, index) => index !== removeIndex);
}

export function toggleAssignedSeatSelection(
  spots: SelectedReservationSpot[],
  targetSpot: ReservableSpot,
): SelectedReservationSpot[] {
  const targetIdentity = getSpotIdentity(targetSpot);
  const isSelected = spots.some((spot) => getSpotIdentity(spot) === targetIdentity);
  if (isSelected) {
    return spots.filter((spot) => getSpotIdentity(spot) !== targetIdentity);
  }

  return [
    ...spots,
    {
      ...targetSpot,
      selectionIndex: 1,
      selectionKey: `${targetIdentity}:assigned`,
    },
  ];
}

export function formatSelectedSpotNames(spots: SelectedReservationSpot[]) {
  const counts = spots.reduce<Record<string, { name: string; count: number }>>((acc, spot) => {
    const identity = getSpotIdentity(spot);
    acc[identity] = {
      name: spot.is_free ? "자유석" : spot.name,
      count: (acc[identity]?.count ?? 0) + 1,
    };
    return acc;
  }, {});

  return Object.values(counts)
    .map(({ name, count }) => (count > 1 ? `${name} x ${count}` : name))
    .join(", ");
}

export function buildReservationRequests({
  phoneNum,
  reservDate,
  reservProduct,
  spots,
  paidAmount,
  notifyUser,
  customerDisplayName = "",
}: ReservationRequestParams) {
  return spots.map((spot) => ({
    phoneNum,
    reservDate,
    reservProduct,
    spot,
    paidAmount,
    notifyUser,
    customerDisplayName,
  }));
}
